// 템플릿 문자열 칸 + 변수 삽입 콤보 — 표시 형식 `template`처럼 `{키}` 치환을 쓰는 칸이 쓴다.
//
// 왜 있나: 템플릿 칸은 맨 텍스트 입력이라 저작자가 쓸 수 있는 키를 **외워서** 쳐야 했다
// (`{location}`이 그 표의 칼럼 키라는 걸 알아야 한다). 오타는 "아무것도 안 나옴"으로만 드러나
// 원인을 알 수 없다 — HybridKeyPicker가 경로 칸에서 푼 문제와 같고, 여기서는 값을 **대체**하는
// 게 아니라 **끼워 넣는다**는 점만 다르다(템플릿은 정적 문구와 변수가 섞인다).
//
// 후보를 강제하지 않는다: 콤보는 삽입 도구일 뿐이고 입력은 언제나 자유 텍스트다(선언이
// 불완전하거나 중첩 경로 `{a.b}`를 써야 할 수 있다).

import { useRef } from 'react';
import { FIELD_LABEL_CLS, INPUT_CLS, SELECT_CLS } from './editorFieldClasses';
import type { HybridKeyOption } from './HybridKeyPicker';

/** 콤보의 자리표시 옵션 값 — 실제 키와 충돌하지 않도록 이중 밑줄 관례(HybridKeyPicker 동형). */
const PICK_PLACEHOLDER = '__pick__';

/** 바인딩된 값 자신을 가리키는 예약어 — 어느 템플릿에서나 유효하다. */
export const TEMPLATE_VALUE_KEY = 'value';

/**
 * 캐럿(또는 선택 범위) 자리에 텍스트를 끼워 넣는다 — 순수 함수(상호작용과 분리해 단위 검증).
 * 범위가 선택돼 있으면 그 범위를 **대체**한다(브라우저 붙여넣기와 같은 감각).
 * 좌표가 없거나(never focused) 범위를 벗어나면 문자열 끝에 덧붙인다.
 */
export function insertAtCaret(
  text: string,
  insert: string,
  start: number | null,
  end: number | null,
): { next: string; caret: number } {
  const len = text.length;
  const s = start === null ? len : Math.max(0, Math.min(start, len));
  const e = end === null ? s : Math.max(s, Math.min(end, len));
  return { next: `${text.slice(0, s)}${insert}${text.slice(e)}`, caret: s + insert.length };
}

/**
 * 템플릿 입력 + "＋변수" 콤보.
 *
 * 캐럿 위치는 자체 ref로 추적한다 — 콤보를 클릭하면 입력이 blur돼 `selectionStart`를 그 시점에
 * 읽을 수 없고, 한 번도 포커스한 적 없는 입력은 0(맨 앞)이라 그대로 쓰면 **앞에 끼워 넣는**
 * 뜻밖의 결과가 된다. 추적값이 없으면 끝에 덧붙인다(빈 칸에 처음 넣을 때의 기대).
 */
export function TemplateVarInput({
  value,
  onChange,
  options,
  placeholder,
  includeValueKey = true,
  label,
}: {
  value: string;
  onChange: (next: string) => void;
  /** 삽입 후보(그 칸의 스코프 필드). 비면 콤보를 숨기고 자유 입력만 남긴다. */
  options: HybridKeyOption[];
  placeholder?: string;
  /** `{value}`(바인딩된 값 자신)를 후보 맨 앞에 넣을지 — 값 개념이 없는 칸은 끈다. */
  includeValueKey?: boolean;
  /**
   * 주면 **라벨 행을 스스로 그리고**(라벨 왼쪽 · 변수 콤보 오른쪽) 입력이 한 행을 다 쓴다.
   * 템플릿은 길어지기 쉬운데 콤보와 한 줄을 나눠 쓰면 입력이 너무 좁다(2026-08-11 사용자 요청).
   * 이때는 BindingRow로 감싸지 말 것 — 라벨이 두 번 나온다.
   * 생략하면 종전 인라인 배치(입력 + 오른쪽 콤보) — 케이스 표처럼 이미 행 안에 있는 칸용.
   */
  label?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  // [start,end] — 마지막으로 관측한 캐럿/선택 범위. null=관측 전(끝에 덧붙임).
  const caretRef = useRef<[number, number] | null>(null);

  const trackCaret = () => {
    const el = inputRef.current;
    if (!el) return;
    const s = el.selectionStart;
    const e = el.selectionEnd;
    caretRef.current = s === null ? null : [s, e ?? s];
  };

  const insertKey = (key: string) => {
    const [s, e] = caretRef.current ?? [null, null];
    const { next, caret } = insertAtCaret(value, `{${key}}`, s, e);
    caretRef.current = [caret, caret];
    onChange(next);
    // 값 반영 뒤에 캐럿을 삽입 지점 뒤로 돌려놓는다 — 연속 삽입이 맨 앞으로 되돌아가지 않게.
    const el = inputRef.current;
    if (el) {
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(caret, caret);
      });
    }
  };

  const pickOptions = includeValueKey
    ? [{ key: TEMPLATE_VALUE_KEY, label: '이 칸의 값 (value)' }, ...options]
    : options;

  const input = (
    <input
      ref={inputRef}
      type="text"
      value={value}
      placeholder={placeholder}
      onChange={(e) => {
        onChange(e.target.value);
        trackCaret();
      }}
      onSelect={trackCaret}
      onKeyUp={trackCaret}
      onClick={trackCaret}
      className={label === undefined ? `${INPUT_CLS} flex-1 min-w-0` : `${INPUT_CLS} w-full`}
    />
  );

  // 값을 갖지 않는 **액션 콤보** — 고르면 삽입하고 즉시 자리표시로 되돌아온다.
  // 네이티브 select라 키보드·모바일 동작을 그냥 얻는다(직접 만든 드롭다운은 바깥 클릭·Esc·
  // 포커스 복귀를 다시 구현해야 하고, 이 프로젝트엔 공용 메뉴 프리미티브가 없다).
  // 닫힌 폭은 고정한다 — 두지 않으면 브라우저가 가장 긴 항목 폭으로 벌린다.
  const picker = pickOptions.length > 0 && (
    <select
      value={PICK_PLACEHOLDER}
      title="커서 위치에 변수를 끼워 넣습니다"
      aria-label="변수 삽입"
      onChange={(e) => {
        const key = e.target.value;
        if (key === PICK_PLACEHOLDER) return;
        insertKey(key);
      }}
      className={`${SELECT_CLS} w-[4.5rem] shrink-0`}
    >
      <option value={PICK_PLACEHOLDER}>＋변수</option>
      {pickOptions.map((o) => (
        <option key={o.key} value={o.key}>
          {o.label}
        </option>
      ))}
    </select>
  );

  if (label !== undefined) {
    return (
      <div>
        <div className="flex items-center justify-between gap-1 mb-0.5">
          <span className={FIELD_LABEL_CLS}>{label}</span>
          {picker}
        </div>
        {input}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      {input}
      {picker}
    </div>
  );
}
